import {
  isActivityFinished,
  type ActivityStatus,
} from "@/common/utils/activityStatus";

/**
 * `DetailActivity` lives in `common/types/global.d.ts`, which this task may not
 * edit, so the two new fields are added as an intersection. The result stays
 * assignable to `DetailActivity`, so existing consumers are untouched.
 */
export type DetailActivityWithStatus = DetailActivity & {
  /** Stored status straight from the API, unmapped. */
  status: ActivityStatus | string;
  /** spec.md 5.1, via the shared predicate. Never a second local rule. */
  isFinished: boolean;
  /** GRC activities owe an LPJ once finished; nothing else does. */
  usesGrc: boolean;
};

interface ActivityDetailResponse {
  data: {
    id: string;
    uuid: string;
    slug: string;
    banner: string;
    bannerUrl: string;
    title: string;
    isMine: boolean;
    endRegistrationDate: string;
    isAlreadyRegistered: boolean;
    creatorPhoneNo: string;
    description: string;
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    address: string;
    assignment: string;
    maxVolunteers: number;
    duration: number;
    criteria: string;
    equipment: string;
    additionalInfo: string;
    attachments: string[];
    attachmentUrls: string[];
    status: string;
    usesGrc: boolean;
    district: {
      id: number;
      cityId: number;
      name: string;
    };
    city: {
      id: number;
      provinceId: number;
      name: string;
    };
    province: {
      id: number;
      name: string;
    };
    registrationClosedDate?: string;
    contactName?: string;
    contactPhone?: string;
  };
  status: string;
  message: string;
  meta: any;
  code: number;
}

export function useActivityDetail() {
  const { $customFetch } = useNuxtApp();
  const config = useRuntimeConfig();
  const useMockData = config.public.useMockData;

  const activityDetail = ref<DetailActivityWithStatus | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const statusCode = ref<number | null>(null);

  const fetchActivityDetail = async (
    activityId: string,
  ): Promise<DetailActivityWithStatus | null> => {
    if (!activityId) {
      error.value = "Activity ID is required";
      statusCode.value = 400;
      return null;
    }

    isLoading.value = true;
    error.value = null;
    statusCode.value = null;

    try {
      if (useMockData) {
        return await mockFetchActivityDetail(activityId);
      } else {
        return await apiFetchActivityDetail(activityId);
      }
    } catch (err: any) {
      console.error("Error fetching activity detail:", err);

      // Extract more detailed error information
      if (err.response) {
        statusCode.value = err.response.status || 500;
        error.value =
          err.response._data?.message || "Failed to load activity details";
      } else {
        statusCode.value = 500;
        error.value =
          err instanceof Error
            ? err.message
            : "Failed to load activity details";
      }

      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const mockFetchActivityDetail = async (
    activityId: string,
  ): Promise<DetailActivityWithStatus> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 800));

    // Single future endDate drives both flags below, so this fixture can't
    // drift into contradicting itself again the way it did with hardcoded
    // 2025 dates that had already fallen into the past: status stays
    // ON_REGISTRATION, endDate stays ahead of "now", and isExpired /
    // isFinished are both derived from that one date instead of being
    // hand-set independently.
    const mockStatus = "ON_REGISTRATION";
    const mockStartDate = new Date(
      Date.now() + 29 * 24 * 60 * 60 * 1000,
    ).toISOString();
    const mockEndDate = new Date(
      Date.now() + 30 * 24 * 60 * 60 * 1000,
    ).toISOString();
    const mockRegistrationDeadline = new Date(
      Date.now() + 20 * 24 * 60 * 60 * 1000,
    ).toISOString();

    // Return mock data
    return {
      id: activityId,
      uuid: "mock-uuid",
      slug: `activity-${activityId}`,
      isWishlist: false,
      creatorPhoneNo: "6281234567890",
      img: "https://picsum.photos/800/400",
      title: "Mock Activity Detail",
      isExpired: new Date(mockEndDate).getTime() < Date.now(),
      status: mockStatus,
      isFinished: isActivityFinished(mockStatus, mockEndDate),
      usesGrc: false,
      isAlreadyRegistered: false,
      isMine: false,
      organizationName: "Mock Organization",
      date: {
        start: mockStartDate,
        end: mockEndDate,
        time: {
          start: "09:00",
          end: "17:00",
        },
        displayText: formatDateRange(mockStartDate, mockEndDate),
      },
      location: {
        name: "Mock Venue",
        address: "Jl. Mock Address No. 123",
        city: "Mock City",
        province: "Mock Province",
      },
      registrationDeadline: mockRegistrationDeadline,
      description: "This is a mock activity description for testing purposes.",
      jobName: "Mock Volunteer",
      totalVolunteers: 10,
      filledSpots: 3,
      totalWorkHours: "8",
      volunteerTasks: "Mock tasks that volunteers will perform.",
      volunteerCriteria: "Mock criteria for volunteer selection.",
      volunteerEquipment: "Mock equipment that volunteers need to bring.",
      additionalInformation: "Additional mock information about the activity.",
      attachmentUrls: [
        "https://cdn.gotongroyong.org/gotongroyong/ce6ec4d4-4aef-4414-91ff-eba1424f1886?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=minioadmin%2F20250404%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20250404T082558Z&X-Amz-Expires=86400&X-Amz-SignedHeaders=host&X-Amz-Signature=b937127e8fc6283ded2a2d7c90eb21cf3c023dc87b285c545b64a18a5f630c0c",
        "https://cdn.gotongroyong.org/gotongroyong/aba5d3a4-3734-4752-b1bd-ab2102814727?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=minioadmin%2F20250404%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20250404T082558Z&X-Amz-Expires=86400&X-Amz-SignedHeaders=host&X-Amz-Signature=829e80ef1e57767c0abcc2544912dca10bc4f437ee57608266e6a1875ffc5f7b",
      ],
      attachments: [
        "ce6ec4d4-4aef-4414-91ff-eba1424f1886",
        "aba5d3a4-3734-4752-b1bd-ab2102814727",
      ],
      volunteers: [
        {
          id: "1",
          name: "Volunteer 1",
          avatarUrl: "https://i.pravatar.cc/150?img=1",
        },
        {
          id: "2",
          name: "Volunteer 2",
          avatarUrl: "https://i.pravatar.cc/150?img=2",
        },
        {
          id: "3",
          name: "Volunteer 3",
          avatarUrl: "https://i.pravatar.cc/150?img=3",
        },
      ],
      contactInfo: {
        name: "Mock Contact",
        whatsapp: "6281234567890",
      },
    };
  };

  const apiFetchActivityDetail = async (
    activitySlug: string,
  ): Promise<DetailActivityWithStatus | null> => {
    try {
      // Directly call the external API
      const response = await $customFetch<ActivityDetailResponse>(
        `/public-activities/${activitySlug}/detail`,
        {
          method: "GET",
        },
      );

      if (response.status !== "success" || !response.data) {
        error.value = response.message || "Failed to fetch activity details";
        return null;
      }

      const data = response.data;

      // Transform API response to match DetailActivity interface
      return {
        id: data.id,
        uuid: data.uuid,
        slug: activitySlug,
        creatorPhoneNo: data.creatorPhoneNo,
        isWishlist: false,
        isAlreadyRegistered: data.isAlreadyRegistered,
        isMine: data.isMine,
        img: data.bannerUrl,
        title: data.title,
        // Legacy marker: kept as-is for registration behaviour, and no longer
        // the thing that decides whether an activity is finished.
        isExpired: data.status !== "ON_REGISTRATION",
        status: data.status,
        // The API already applies the 5.1 rule to `status` (activity.service.ts:938
        // -> getActivityStatusExternal), comparing real timestamps. `endDate` here
        // is date-only, so re-deriving from it would mark an activity finished up
        // to a day early. Trust the server's computed status.
        isFinished: isActivityFinished(data.status, null),
        usesGrc: Boolean(data.usesGrc),
        organizationName: "", // Not provided in API response
        date: {
          start: data.startDate,
          end: data.endDate,
          time: {
            start: data.startTime,
            end: data.endTime,
          },
          displayText: formatDateRange(data.startDate, data.endDate),
        },
        location: {
          name: "",
          address: data.address,
          city: data.city?.name || "",
          province: data.province?.name || "",
        },
        registrationDeadline: data.endRegistrationDate,
        description: data.description,
        jobName: data.assignment,
        totalVolunteers: data.maxVolunteers,
        filledSpots: 0, // Not provided in API response
        // `duration` is nullable in the database, so calling .toString() on it
        // threw during SSR and turned the whole detail page into the error state.
        totalWorkHours: data.duration == null ? "" : String(data.duration),
        volunteerTasks: data.assignment,
        volunteerCriteria: data.criteria,
        volunteerEquipment: data.equipment,
        additionalInformation: data.additionalInfo,
        attachments: data.attachments,
        attachmentUrls: data.attachmentUrls,
        volunteers: [], // Not provided in API response
        contactInfo: {
          name: data.contactName || "Contact Person",
          whatsapp: formatPhoneNumber(data.contactPhone || ""),
        },
      };
    } catch (error) {
      console.error("Error in apiFetchActivityDetail:", error);
      throw error;
    }
  };

  // Helper function to format date range
  const formatDateRange = (startDate: string, endDate: string): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // If same day event
    if (start.toDateString() === end.toDateString()) {
      return new Intl.DateTimeFormat("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(start);
    }

    // If same month event
    if (
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear()
    ) {
      return `${start.getDate()} - ${new Intl.DateTimeFormat("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(end)}`;
    }

    // Different months
    return `${new Intl.DateTimeFormat("id-ID", {
      day: "numeric",
      month: "long",
    }).format(start)} - ${new Intl.DateTimeFormat("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(end)}`;
  };

  // Helper function to format phone number
  const formatPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber) return "";

    // Ensure the phone number starts with country code
    if (phoneNumber.startsWith("0")) {
      return "62" + phoneNumber.substring(1);
    }

    if (!phoneNumber.startsWith("+") && !phoneNumber.startsWith("62")) {
      return "62" + phoneNumber;
    }

    if (phoneNumber.startsWith("+")) {
      return phoneNumber.substring(1); // Remove the + sign
    }

    return phoneNumber;
  };

  // Helper function to generate a default deadline if none is provided
  const getDefaultDeadline = (startDate: string): string => {
    // Default to 1 day before the start date
    const date = new Date(startDate);
    date.setDate(date.getDate() - 1);
    return date.toISOString();
  };

  return {
    activityDetail,
    isLoading,
    error,
    statusCode,
    fetchActivityDetail,
  };
}
